import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import Swal from 'sweetalert2';

const settingsSchema = z.object({
  defaultCurrency: z.enum(['EUR', 'USD', 'TRY', 'RUB']),
  defaultLocale: z.enum(['en', 'de', 'tr', 'ru']),
  systemName: z.string().min(1, 'System name is required'),
  primaryColor: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid color format'),
});

const Settings = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('general');

  // Fetch settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/settings');
      return response.data;
    },
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put('/settings/my', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('settings.settingsSaved'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Settings update error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('settings.settingsSaveFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(settingsSchema),
    defaultValues: settings || {
      defaultCurrency: 'EUR',
      defaultLocale: 'en',
      systemName: 'Invoice Management System',
      primaryColor: '#3b82f6',
    },
  });

  const watchedColor = watch('primaryColor');

  // Reset form when settings are loaded
  useEffect(() => {
    if (settings) {
      reset(settings);
    }
  }, [settings, reset]);

  // Apply primary color to CSS variables
  useEffect(() => {
    if (watchedColor) {
      document.documentElement.style.setProperty('--primary-600', watchedColor);
      document.documentElement.style.setProperty('--primary-700', adjustColor(watchedColor, -20));
      document.documentElement.style.setProperty('--primary-500', adjustColor(watchedColor, 20));
    }
  }, [watchedColor]);

  const adjustColor = (color, amount) => {
    const num = parseInt(color.replace("#", ""), 16);
    const r = Math.max(0, Math.min(255, ((num >> 16) & 255) + amount));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 255) + amount));
    const b = Math.max(0, Math.min(255, (num & 255) + amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  const onSubmit = (data) => {
    updateMutation.mutate(data);
  };

  const handleLanguageChange = (locale) => {
    i18n.changeLanguage(locale);
    localStorage.setItem('locale', locale);
  };

  // Database backup mutation
  const backupMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/settings/backup', {}, {
        responseType: 'blob'
      });
      return response.data;
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      Swal.fire({
        icon: 'success',
        title: t('settings.backupSuccess'),
        text: t('settings.backupCreated'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('settings.backupFailed'),
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Database restore mutation
  const restoreMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('backup', file);
      const response = await api.post('/settings/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: t('settings.restoreSuccess'),
        text: t('settings.restoreCompleted'),
        confirmButtonText: t('common.ok')
      }).then(() => {
        window.location.reload();
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('settings.restoreFailed'),
        confirmButtonText: t('common.ok')
      });
    },
  });

  // CSV export mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get('/settings/export-csv', {
        responseType: 'blob'
      });
      return response.data;
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_data_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      Swal.fire({
        icon: 'success',
        title: t('settings.exportSuccess'),
        text: t('settings.exportCompleted'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('settings.exportFailed'),
        confirmButtonText: t('common.ok')
      });
    },
  });

  // CSV import mutation
  const importMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('csv', file);
      const response = await api.post('/settings/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: t('settings.importSuccess'),
        text: t('settings.importCompleted'),
        confirmButtonText: t('common.ok')
      }).then(() => {
        queryClient.invalidateQueries();
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('settings.importFailed'),
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Server restart mutation
  const restartMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/settings/restart-server');
      return response.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: t('settings.restartSuccess'),
        text: t('settings.restartCompleted'),
        confirmButtonText: t('common.ok')
      }).then(() => {
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('settings.restartFailed'),
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Cache clear mutation
  const clearCacheMutation = useMutation({
    mutationFn: async (type) => {
      const response = await api.post('/settings/clear-cache', { type });
      return response.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: t('settings.cacheCleared'),
        text: t('settings.cacheClearedSuccess'),
        confirmButtonText: t('common.ok')
      });
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('settings.cacheClearFailed'),
        confirmButtonText: t('common.ok')
      });
    },
  });

  const handleFileUpload = (file, type) => {
    if (type === 'restore') {
      restoreMutation.mutate(file);
    } else if (type === 'import') {
      importMutation.mutate(file);
    }
  };

  const tabs = [
    { id: 'general', name: t('settings.general'), icon: '⚙️' },
    { id: 'backup', name: t('settings.backupRestore'), icon: '💾' },
    { id: 'import', name: t('settings.importExport'), icon: '📊' },
    { id: 'system', name: t('settings.system'), icon: '🖥️' },
    { id: 'cache', name: t('settings.cache'), icon: '🗑️' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('settings.description')}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'general' && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('settings.general')}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">{t('settings.defaultCurrency')}</label>
                  <select {...register('defaultCurrency')} className="input">
                    <option value="EUR">EUR - Euro</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="TRY">TRY - Turkish Lira</option>
                    <option value="RUB">RUB - Russian Ruble</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('settings.defaultLanguage')}</label>
                  <select
                    {...register('defaultLocale')}
                    onChange={(e) => {
                      register('defaultLocale').onChange(e);
                      handleLanguageChange(e.target.value);
                    }}
                    className="input"
                  >
                    <option value="en">🇺🇸 English</option>
                    <option value="de">🇩🇪 Deutsch</option>
                    <option value="tr">🇹🇷 Türkçe</option>
                    <option value="ru">🇷🇺 Русский</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="label">{t('settings.systemName')}</label>
                  <input
                    {...register('systemName')}
                    type="text"
                    className="input"
                    placeholder={t('settings.systemNamePlaceholder')}
                  />
                  {errors.systemName && (
                    <p className="text-red-500 text-sm mt-1">{errors.systemName.message}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="label">{t('settings.primaryColor')}</label>
                  <div className="flex items-center space-x-3">
                    <input
                      {...register('primaryColor')}
                      type="color"
                      className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      onChange={(e) => {
                        register('primaryColor').onChange(e);
                        setValue('primaryColor', e.target.value);
                      }}
                    />
                    <input
                      {...register('primaryColor')}
                      type="text"
                      className="input flex-1"
                      placeholder="#3b82f6"
                      onChange={(e) => {
                        register('primaryColor').onChange(e);
                        setValue('primaryColor', e.target.value);
                      }}
                    />
                    <div 
                      className="w-8 h-8 rounded border border-gray-300"
                      style={{ backgroundColor: watchedColor }}
                    ></div>
                  </div>
                  {errors.primaryColor && (
                    <p className="text-red-500 text-sm mt-1">{errors.primaryColor.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="btn btn-primary"
              >
                {updateMutation.isPending ? t('common.loading') : t('settings.saveSettings')}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'backup' && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('settings.databaseBackup')}</h2>
              <div className="space-y-4">
                <div>
                  <button
                    onClick={() => backupMutation.mutate()}
                    disabled={backupMutation.isPending}
                    className="btn btn-primary"
                  >
                    {backupMutation.isPending ? t('common.loading') : t('settings.createBackup')}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.backupDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('settings.databaseRestore')}</h2>
              <div className="space-y-4">
                <div>
                  <input
                    type="file"
                    accept=".json,.sql"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload(file, 'restore');
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.restoreDescription')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'import' && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('settings.csvExport')}</h2>
              <div className="space-y-4">
                <div>
                  <button
                    onClick={() => exportMutation.mutate()}
                    disabled={exportMutation.isPending}
                    className="btn btn-primary"
                  >
                    {exportMutation.isPending ? t('common.loading') : t('settings.exportData')}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.exportDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('settings.csvImport')}</h2>
              <div className="space-y-4">
                <div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFileUpload(file, 'import');
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.importDescription')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('settings.serverRestart')}</h2>
              <div className="space-y-4">
                <div>
                  <button
                    onClick={() => {
                      Swal.fire({
                        title: t('settings.confirmRestart'),
                        text: t('settings.restartWarning'),
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#d33',
                        cancelButtonColor: '#3085d6',
                        confirmButtonText: t('settings.yesRestart'),
                        cancelButtonText: t('common.cancel')
                      }).then((result) => {
                        if (result.isConfirmed) {
                          restartMutation.mutate();
                        }
                      });
                    }}
                    disabled={restartMutation.isPending}
                    className="btn btn-danger"
                  >
                    {restartMutation.isPending ? t('common.loading') : t('settings.restartServer')}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.restartDescription')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cache' && (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('settings.cacheManagement')}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <button
                    onClick={() => clearCacheMutation.mutate('browser')}
                    disabled={clearCacheMutation.isPending}
                    className="btn btn-secondary w-full"
                  >
                    {clearCacheMutation.isPending ? t('common.loading') : t('settings.clearBrowserCache')}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.browserCacheDescription')}
                  </p>
                </div>
                <div>
                  <button
                    onClick={() => clearCacheMutation.mutate('application')}
                    disabled={clearCacheMutation.isPending}
                    className="btn btn-secondary w-full"
                  >
                    {clearCacheMutation.isPending ? t('common.loading') : t('settings.clearAppCache')}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.appCacheDescription')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;